import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface Nature {
  id: number;
  natureName: string;
  status: boolean | string;
}

export default function Natures() {
  const navigate = useNavigate();
  const [natures, setNatures] = useState<Nature[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getNatures();
  }, [page, limit, search]);

  const getNatures = async () => {
    setLoading(true);
    const response = await request.get("/api/getNatures", {
      search,
      limit,
      offset: page,
      orderColumn: "natureName",
      orderDirection: "asc",
    });
    setNatures(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "natureName",
      label: "Nature Name",
      minWidth: 200,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => (
        <StatusChip active={value === true || value === "Active"} />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/natures/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({
                  open: true,
                  id: row.id,
                  title: row?.natureName,
                }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Natures"
        showSearch
        searchPlaceholder="Search natures..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Nature",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/natures/create");
          },
        }}
      />

      <DataTable
        columns={columns}
        rows={natures}
        totalCount={totalCount}
        onPaginationChange={handlePaginationChange}
        page={page}
        limit={limit}
      />
      <DeleteConfirmation
        open={deleteDialog.open}
        title={"Delete Nature - " + deleteDialog?.title}
        message="Are you sure you want to delete this nature? This action cannot be undone."
        apiEndpoint={"/api/deleteNature/"}
        deleteId={deleteDialog.id}
        onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
        refetchApi={() => {
          setPage(0);
          getNatures();
        }}
      />
    </PageWrapper>
  );
}
